import { Inject, Injectable } from '@angular/core';
import { StatisticItemEnum } from '@iv/shared/enums/statistic-item.enum';
import { HttpClient } from '@angular/common/http';
import Utils from '@iv/shared/utils';
import { getCurrentTab } from '@iv/store/tabs/tabs.selectors';
import { AppStore } from '@iv/store/app.store';
import * as Redux from 'redux';
import { AppState } from '@iv/store/app.reducer';
import { CommonService } from '../core/services/common.service';
import { IUserSubscription } from '../core/interfaces/user-settings.interface';

interface ITimeServicesStatistic extends IUserData {
  seconds: string;
  service: string;
  subscription: string;

  countPageView: string;
}

interface ITimeSpendedStatistic extends IUserData {
  seconds: string;
  pageName: string;
}

interface IButtonStatistic extends IUserData {
  buttonName: string;
  counter: string;
  component: string;
}

interface IPageStatistic extends IUserData {
  pageName: string;
  counter: string;
}

interface IUserData {
  uuid: string;
  userId: string;
}

interface IChartsStatistic extends IUserData{

  chartName: string;
  counter: string;

}

@Injectable({providedIn: 'root'})
export class StatisticService {

  public countPageView = 'false';

  public constructor(
    private httpClient: HttpClient,
    private commonService: CommonService,
    @Inject(AppStore) private store: Redux.Store<AppState>
  ) { }

  public sendStatistic(): void {
    const countStorageButton: string = localStorage.getItem(StatisticItemEnum.BUTTON_COUNT);
    const countStorageTab: string = localStorage.getItem(StatisticItemEnum.TAB_COUNT);
    const countPageTime: string = localStorage.getItem(StatisticItemEnum.PAGE_COUNT);
    const countChart: string = localStorage.getItem(StatisticItemEnum.CHART_COUNT);
    const countService: string = localStorage.getItem(StatisticItemEnum.SERVICE_COUNT);

    if (countStorageButton) {
      const countObject: any = JSON.parse(countStorageButton);
      for (const key of Object.keys(countObject)) {
        const params: IButtonStatistic = {
          buttonName: key.slice(0, key.indexOf(', ')),
          component: key.slice(key.indexOf(',') + 12, key.length),
          counter: countObject[key],
          uuid: Utils.getUserUuid(),
          userId: Utils.getUserId()
        };
        this.httpClient.post<void>('/user-metrics/buttonCount', params)
          .subscribe(() => localStorage.removeItem(StatisticItemEnum.BUTTON_COUNT));
      }
    }

    if (countStorageTab) {
      const countObject: any = JSON.parse(countStorageTab);
      for (const key of Object.keys(countObject)) {
        const params: IPageStatistic = {
          pageName: `${this.formatNameTab(getCurrentTab(this.store.getState()).component.toString())} ${this.formatNameTab(getCurrentTab(this.store.getState()).name)}`,
          counter: countObject[key],
          uuid: Utils.getUserUuid(),
          userId: Utils.getUserId()
        };
        this.httpClient.post<void>('/user-metrics/pageCount', params)
          .subscribe(() => localStorage.removeItem(StatisticItemEnum.TAB_COUNT));
      }
    }

    if (countPageTime) {
      const countObject: any = JSON.parse(countPageTime);
      for (const key of Object.keys(countObject)) {
        const params: ITimeSpendedStatistic = {
          seconds: countObject[key],
          uuid: Utils.getUserUuid(),
          userId: Utils.getUserId(),
          pageName: `${this.formatNameTab(getCurrentTab(this.store.getState()).component.toString())} ${this.formatNameTab(getCurrentTab(this.store.getState()).name)}`
        };
        this.httpClient.post<void>('/user-metrics/timeSpent', params)
          .subscribe(() => localStorage.removeItem(StatisticItemEnum.PAGE_COUNT));
      }

    }

    if (countService) {
      var service: string;
      if (
        (this.commonService.userSubscriptions
            .find((sub: IUserSubscription) => (sub.serviceId === 1005 && sub.subscriptionCancelTypeId === 1))
        )) {
        service = 'Basic';
      }

      if (
        (this.commonService.userSubscriptions
            .find((sub: IUserSubscription) => (sub.serviceId === 1007 && sub.subscriptionCancelTypeId === 1))
        )) {
        service = 'Advanced';
      }

      if (
        (this.commonService.userSubscriptions
            .find((sub: IUserSubscription) => (sub.serviceId === 1008 && sub.subscriptionCancelTypeId === 1))
        )) {
        service = 'Pro';
      }

      const countObject: any = JSON.parse(countService);
      for (const key of Object.keys(countObject)) {
        const params: ITimeServicesStatistic = {
          seconds: countObject[key],
          uuid: Utils.getUserUuid(),
          userId: Utils.getUserId(),
          service:  `${this.formatNameTab(getCurrentTab(this.store.getState()).component.toString())} ${this.formatNameTab(getCurrentTab(this.store.getState()).name)}`,
          subscription: service,
          countPageView: this.countPageView
        };
        this.httpClient.post<void>('/user-metrics/serviceStatistic', params)
          .subscribe(() => {
            localStorage.removeItem(StatisticItemEnum.SERVICE_COUNT);
            this.countPageView = 'true';
          });
      }

    }

    if (countChart) {
      const countObject: any = JSON.parse(countChart);
      for (const key of Object.keys(countObject)) {
        const params: IChartsStatistic = {
          chartName: key,
          counter: countObject[key],
          uuid: Utils.getUserUuid(),
          userId: Utils.getUserId()
        };
        this.httpClient.post<void>('/user-metrics/chartStatistic', params)
          .subscribe(() => localStorage.removeItem(StatisticItemEnum.CHART_COUNT));
      }
    }
  }

  public incrementPageTime(): void {
    this.save(StatisticItemEnum.PAGE_COUNT, StatisticItemEnum.PAGE_COUNT);
  }

  public incrementServiceTime(): void {
    this.save(StatisticItemEnum.SERVICE_COUNT, StatisticItemEnum.SERVICE_COUNT);
  }

  public incrementClickChart(chartName: string): void {
    this.save(chartName, StatisticItemEnum.CHART_COUNT);
  }

  public incrementClickButton(button: string): void {
    this.save(button, StatisticItemEnum.BUTTON_COUNT);
  }

  public incrementViewTab(componentName: string): void {
    this.save(componentName, StatisticItemEnum.TAB_COUNT);
    this.countPageView = 'false';
  }

  private formatNameTab(componentName: string): string {
    switch (componentName) {
      case 'ChartsDashboardComponent': return 'Charts';
      case 'OptionChainComponent': return 'Stocks Options Chain';
      case 'FavouritesComponent': return 'Stock Monitor';
      case 'FutureOptionChainComponent': return 'Futures Options Chain';
      case 'FuturesMonitorComponent': return 'Futures Monitor';
      case 'WorkspaceComponent': return 'Options Chain';
      case 'VX chart click Charts': return 'IVX Monitor';
      case 'chart click Charts': return 'IVX Monitor';
      case 'chart click Table': return 'IVX Monitor';
      case 'VX chart click Table': return 'IVX Monitor';
    }
    return componentName;
  }

  private save(field: string, keyLocalStorage): void {
    const countStorage: string = localStorage.getItem(keyLocalStorage);
    let countObject: any = {};
    if (countStorage != null) {
      countObject = JSON.parse(countStorage);
    }
    if (countObject[field]) {
      countObject[field] = countObject[field] + 1;
    } else {
      countObject[field] = 1;
    }
    localStorage.setItem(keyLocalStorage, JSON.stringify(countObject));
  }
}
